import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { ArrowLeft, Loader2 } from "lucide-react";

import { GET_STUDENT } from "@/graphql/queries/students";
import { UPDATE_STUDENT } from "@/graphql/mutations/students";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { exerciseCountValue, subjectExerciseCounts } from "@/lib/students";
import { StudentFormFields } from "@/components/students/student-form";
import { toast } from "sonner";

export default function EditStudentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading } = useQuery(GET_STUDENT, { variables: { id }, skip: !id });
  const [updateStudent] = useMutation(UPDATE_STUDENT);
  const [isSaving, setIsSaving] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const student = data?.student;

  if (!student) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Mokinys nerastas</p>
        <Button asChild className="mt-4">
          <Link to="/students">Grįžti į sąrašą</Link>
        </Button>
      </div>
    );
  }

  const subjectLengths = student.subjectSessionLengths ?? [];

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSaving(true);

    const formData = new FormData(event.currentTarget);
    // Sent as a number or as an explicit null — never omitted. An omitted field
    // means "leave it alone" to the server, so emptying one has to say null or
    // the old value quietly survives.
    const number = (field: string) => {
      const raw = (formData.get(field) as string)?.trim();
      return raw ? parseInt(raw, 10) : null;
    };
    const input = {
      name: formData.get("name") as string,
      birthYear: number("birthYear"),
      birthMonth: number("birthMonth"),
      birthDay: number("birthDay"),
      firstGradeYear: number("firstGradeYear"),
      // An empty string, never null: the server reads null as "leave it alone",
      // so emptying the subtitle has to say so or the old one survives.
      notes: ((formData.get("notes") as string) ?? "").trim(),
      defaultExerciseCount: exerciseCountValue(formData.get("defaultExerciseCount")),
      // Driven by the subjects the form rendered, not by what it sent: an
      // emptied box has to arrive as 0 (clear this limit) rather than as nothing
      // at all, which the server reads as "leave it alone".
      subjectExerciseCounts: subjectExerciseCounts(
        formData,
        subjectLengths.map((subject: { subjectKey: string }) => subject.subjectKey)
      ),
      // Radix's checkbox submits "on" when ticked and nothing at all when not,
      // so this is a real false rather than an omission the server would read as
      // "leave it alone".
      requireDiscussionResolution: formData.get("requireDiscussionResolution") === "on",
      email: (formData.get("email") as string) ?? "",
      // Sent only when something was typed: the server reads an empty string as
      // "remove the password", which is not what an untouched field means.
      password: ((formData.get("password") as string) || undefined),
    };

    try {
      const { data: result } = await updateStudent({ variables: { id, ...input } });
      const errors: string[] = result?.updateStudent?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors.join("; "));
        return;
      }
      // A changed address costs the learner their sign-in until they confirm the
      // new one, so a save that did that cannot pass as an ordinary save.
      if (result?.updateStudent?.emailed) {
        toast.success("Duomenys atnaujinti. Naują el. paštą reikia patvirtinti", {
          description:
            "Nuorodą išsiuntėme nauju adresu. Kol jos nepaspaus, mokinys su juo neprisijungs.",
        });
      } else {
        toast.success("Mokinio duomenys atnaujinti");
      }
      navigate(`/students/${id}`);
    } catch {
      toast.error("Nepavyko atnaujinti mokinio");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/students/${id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{student.name}</h1>
          <p className="text-muted-foreground">Mokinio duomenys</p>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <Card className="max-w-3xl gap-0 py-0">
          <StudentFormFields
            disabled={isSaving}
            defaults={{
              name: student.name,
              birthYear: student.birthYear,
              birthMonth: student.birthMonth,
              birthDay: student.birthDay,
              firstGradeYear: student.firstGradeYear,
              email: student.email,
              hasPassword: student.hasPassword,
              notes: student.notes,
              defaultExerciseCount: student.defaultExerciseCount,
              suggestedExerciseCount: student.suggestedExerciseCount,
              subjectSessionLengths: subjectLengths,
              requireDiscussionResolution: student.requireDiscussionResolution,
            }}
          />

          <div className="flex flex-wrap gap-2 border-t px-6 py-4">
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Išsaugoti
            </Button>
            <Button type="button" variant="ghost" asChild>
              <Link to={`/students/${id}`}>Atšaukti</Link>
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
